// App.js
import Particles from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import { Analytics } from "@vercel/analytics/react";
import { motion } from "framer-motion";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaVideo } from "react-icons/fa"; // Video Icon from FontAwesome
import "./App.css";
import AudioPlayer from "./components/AudioPlayer/AudioPlayer";
import SocialIcons from "./components/SocialIcons/SocialIcons";
import { ShootingStars } from "./components/ui/shooting-stars";

function App() {
  const [isPlaying, setIsPlaying] = useState(false); // Sync with audio
  const [videoPlaying, setVideoPlaying] = useState(false); // Track video background
  const [isTransitioning, setIsTransitioning] = useState(false); // Track transition
  const videoRef = useRef(null); // Reference to video element

  const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 });
  const [canClick, setCanClick] = useState(true);

  // Replace clouds with blobs
  const blobs = [
    { id: 1 },
    { id: 2 },
    { id: 3 },
    { id: 4 }
  ];

  // Initialize particles
  const particlesInit = useCallback(async (engine) => {
    await loadSlim(engine);
  }, []);

  const particlesOptions = {
    background: {
      color: { value: "#1a1625" }, // Match new background
    },
    fpsLimit: 60,
    interactivity: {
      events: {
        onClick: {
          enable: true,
          mode: "push",
        },
        onHover: {
          enable: true,
          mode: "bubble",
        },
        resize: true,
      },
      modes: {
        bubble: {
          distance: 250,
          size: 10,
          duration: 2,
          opacity: 0.8,
          speed: 3,
        },
        push: {
          quantity: 4,
        },
      },
    },
    particles: {
      color: {
        value: [
          "#9b6b8d", // Muted pink
          "#6b8d9b", // Muted blue
          "#8d6b9b", // Muted purple
          "#6b9b8d", // Muted green
        ],
      },
      links: {
        color: "#8d6b9b", // Muted purple for links
        distance: 150,
        enable: true,
        opacity: 0.2, // More subtle connections
        width: 1,
      },
      move: {
        enable: true,
        speed: 1,
        straight: false,
      },
      number: {
        density: {
          enable: true,
          area: 800,
        },
        value: 60,
      },
      opacity: {
        value: 0.4, // Less visible particles
        random: true,
      },
      shape: {
        type: "circle",
      },
      size: {
        value: { min: 2, max: 5 },
      },
    },
    detectRetina: true,
  };

  const toggleVideoBackground = useCallback(() => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    if (!videoPlaying && videoRef.current) {
      setVideoPlaying(true);
      videoRef.current
        .play()
        .then(() => {
          setIsTransitioning(false);
        })
        .catch((err) => {
          console.error("Video play error:", err);
          setIsTransitioning(false);
        });
    } else if (videoRef.current) {
      videoRef.current.pause();
      setVideoPlaying(false);
      setIsTransitioning(false);
    }
  }, [isTransitioning, videoPlaying]);

  const handleVideoEnd = useCallback(() => {
    setVideoPlaying(false);
  }, []);

  const handlePlayPause = useCallback((playing) => {
    setIsPlaying(playing);
  }, []);

  // Replace the createRipple function with this new implementation
  const createClickEffect = useCallback((event) => {
    if (!canClick) return;
    setCanClick(false);

    const container = document.createElement('div');
    container.className = 'click-effect';
    container.style.left = `${event.clientX}px`;
    container.style.top = `${event.clientY}px`;
    document.body.appendChild(container);

    // Create expanding rings
    const numRings = 3;
    for (let i = 0; i < numRings; i++) {
      const ring = document.createElement('div');
      ring.className = 'magnetic-ring';
      ring.style.animationDelay = `${i * 0.2}s`;
      container.appendChild(ring);
    }

    // Create floating particles
    const numParticles = 12;
    const radius = 100;

    for (let i = 0; i < numParticles; i++) {
      const particle = document.createElement('div');
      particle.className = 'magnetic-particle';

      // Calculate start position (on a circle)
      const angle = (i / numParticles) * Math.PI * 2;
      const startX = Math.cos(angle) * 10;
      const startY = Math.sin(angle) * 10;

      // Calculate end position (random direction)
      const endAngle = angle + (Math.random() * 0.5 - 0.25) * Math.PI;
      const endX = Math.cos(endAngle) * radius;
      const endY = Math.sin(endAngle) * radius;

      particle.style.setProperty('--startX', `${startX}px`);
      particle.style.setProperty('--startY', `${startY}px`);
      particle.style.setProperty('--endX', `${endX}px`);
      particle.style.setProperty('--endY', `${endY}px`);

      particle.style.animation = `particleFloat ${0.8 + Math.random() * 0.4}s cubic-bezier(0.2, 0, 0.1, 1) forwards`;
      container.appendChild(particle);
    }

    // Cleanup
    setTimeout(() => {
      container.remove();
      setCanClick(true);
    }, 2000);
  }, [canClick]);

  useEffect(() => {
    document.addEventListener('click', createClickEffect);
    return () => document.removeEventListener('click', createClickEffect);
  }, [createClickEffect]);

  // Enhanced cursor tracking for spotlight and gradient
  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const x = (clientX / window.innerWidth) * 100;
      const y = (clientY / window.innerHeight) * 100;
      setCursorPos({ x, y });

      // Update CSS variables for gradient position
      document.documentElement.style.setProperty('--x', `${x}%`);
      document.documentElement.style.setProperty('--y', `${y}%`);

      // Update spotlight position
      const spotlight = document.querySelector('.spotlight');
      if (spotlight) {
        spotlight.style.left = `${clientX}px`;
        spotlight.style.top = `${clientY}px`;
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <div className="App">
      {/* Dramatic Spotlight */}
      <div className="dramatic-spotlight" />

      {/* Shooting Stars Effects */}
      <ShootingStars
        starColor="#ffb8d9"
        trailColor="#b8c0ff"
        minSpeed={15}
        maxSpeed={35}
        minDelay={1000}
        maxDelay={3000}
      />
      <ShootingStars
        starColor="#ffd5b8"
        trailColor="#ffb8d9"
        minSpeed={10}
        maxSpeed={25}
        minDelay={2000}
        maxDelay={4000}
      />
      <ShootingStars
        starColor="#b8c0ff"
        trailColor="#eeb8ff"
        minSpeed={20}
        maxSpeed={40}
        minDelay={1500}
        maxDelay={3500}
      />

      {/* Spotlight Effect */}
      <div className="spotlight" />

      {/* Gradient Blobs */}
      {blobs.map((blob) => (
        <div
          key={blob.id}
          className="blob"
        />
      ))}

      {/* Particle Background */}
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={{
          ...particlesOptions,
          particles: {
            ...particlesOptions.particles,
            color: {
              value: [
                "#ffb8d9", // Pastel pink
                "#b8c0ff", // Pastel lavender
                "#ffd5b8", // Pastel orange
                "#eeb8ff", // Pastel purple
              ],
            },
            links: {
              ...particlesOptions.particles.links,
              color: "#ffb8d9", // Pastel pink for links
              opacity: 0.15, // More subtle connections
            },
          },
        }}
      />

      {/* Background Video */}
      <video
        ref={videoRef}
        className={`background-video ${videoPlaying ? "active" : ""}`}
        onEnded={handleVideoEnd}
        loop={false}
        muted
        playsInline
      >
        <source src="/background.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Main Content */}
      <motion.div
        className={`content ${isPlaying ? "pulse" : ""} ${
          videoPlaying ? "video-mode" : ""
        }`}
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1 }}
      >
        <motion.h1
          className="name"
          aria-label="Miquella"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
        >
          miquella
        </motion.h1>

        <motion.p
          className="description"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
        >
          Crafting Dreams into Reality
        </motion.p>

        <SocialIcons isPlaying={isPlaying} videoPlaying={videoPlaying} />
      </motion.div>

      {/* Controls Container - Moved outside content */}
      <div className="controls-container">
        <motion.button
          className="video-icon"
          onClick={toggleVideoBackground}
          aria-label={
            videoPlaying ? "Pause Video Background" : "Play Video Background"
          }
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <FaVideo size={22} />
        </motion.button>

        <AudioPlayer onPlayPause={handlePlayPause} />
      </div>

      <Analytics />
    </div>
  );
}

export default App;
